// Utility to crop an image given the source url and the pixel crop area.
// Returns a Blob of the cropped image in the requested format (default JPEG without alpha).
// Note: face-recognition (dlib) expects an 8-bit, 3-channel RGB image. JPEG ensures no alpha channel.

export default async function getCroppedImg(
  imageSrc: string,
  pixelCrop: { x: number; y: number; width: number; height: number },
  outputType: 'image/jpeg' | 'image/png' = 'image/jpeg',
  quality = 0.92
): Promise<Blob> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const width = Math.max(1, Math.round(pixelCrop.width));
  const height = Math.max(1, Math.round(pixelCrop.height));
  canvas.width = width;
  canvas.height = height;

  // Request a 2D context with alpha=false to force opaque rendering when possible
  const ctx = canvas.getContext('2d', { alpha: false }) as CanvasRenderingContext2D | null;
  if (!ctx) throw new Error('Could not get canvas context');

  // If the output is JPEG (no alpha), pre-fill with white background so transparent images won't produce black/undefined pixels
  if (outputType === 'image/jpeg') {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  ctx.drawImage(
    image,
    Math.round(pixelCrop.x),
    Math.round(pixelCrop.y),
    Math.round(pixelCrop.width),
    Math.round(pixelCrop.height),
    0,
    0,
    width,
    height
  );

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas is empty'));
      },
      outputType,
      // Only JPEG honors quality parameter. PNG ignores it.
      quality
    );
  });
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', (error) => reject(error));
    img.setAttribute('crossOrigin', 'anonymous'); // needed to avoid CORS issues on Canvas
    img.src = url;
  });
}
